import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarIcon, Search, X, RefreshCw, FileSpreadsheet, FileText,
  Gem, Package, CheckSquare2, Square
} from 'lucide-react';
import { diamondsService } from '@/services/diamonds.service';
import { useAuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getSubdomainFranchise } from '@/utils/get-subdomain';

const IMAGE_BASE_URL = 'https://xdoinfinitto.com.br:3000';

// Interface para os dados de resgate retornados pela API
interface ResgateItem {
  id: number;
  user_id: number;
  venda_id: number | null;
  quantity: number;
  action: 'resgate_pendente' | 'resgate_aprovado' | 'resgate_reprovado';
  observation: string | null;
  item_id: number;
  date_expiration: string;
  date_created: string;
  codepagarme: string | null;
  nome_cliente: string | null;
  item_nome: string;
  item_imagem: string;
  nome_razao_social: string;
  codigo: string;
  role?: string;
}

// Helpers
const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return '-';
  }
};

const getActionLabel = (action: string): string => {
  switch (action) {
    case 'resgate_pendente': return 'Pendente';
    case 'resgate_aprovado': return 'Aprovado';
    case 'resgate_reprovado': return 'Reprovado';
    default: return action;
  }
};

const getActionStyles = (action: string) => {
  switch (action) {
    case 'resgate_pendente':
      return { badge: 'bg-yellow-100 text-yellow-800 border-yellow-300', row: 'bg-yellow-50' };
    case 'resgate_aprovado':
      return { badge: 'bg-green-100 text-green-800 border-green-300', row: 'bg-green-50' };
    case 'resgate_reprovado':
      return { badge: 'bg-red-100 text-red-800 border-red-300', row: 'bg-red-50' };
    default:
      return { badge: 'bg-gray-100 text-gray-800 border-gray-300', row: '' };
  }
};

export default function RelatoriosResgates() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const franquia = getSubdomainFranchise();
  const isMatriz = franquia === 'matriz';

  const [resgates, setResgates] = useState<ResgateItem[]>([]);
  const [filteredResgates, setFilteredResgates] = useState<ResgateItem[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>('resgate_pendente');

  // Paginação client-side
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Seleção em massa
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkLoading, setIsBulkLoading] = useState(false);

  // Debounce da busca
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadResgates = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await diamondsService.getStatementsWithdrawal();
      if (data && Array.isArray(data)) {
        setResgates(data);
      } else {
        setResgates([]);
      }
    } catch (error) {
      console.error('Erro ao buscar resgates:', error);
      toast.error('Erro ao buscar relatório de resgates');
      setResgates([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Aplica filtros locais
  useEffect(() => {
    let filtered = [...resgates];

    // Filtro por situação
    if (actionFilter) {
      filtered = filtered.filter(r => r.action === actionFilter);
    }

    // Filtro por busca textual
    if (searchFilter) {
      const search = searchFilter.trim().toLowerCase();
      // Suporte para busca por data formatada dd/MM
      if (search.includes('/')) {
        const dateParts = search.split('/');
        const formattedDate = `${new Date().getFullYear()}-${dateParts[1]?.padStart(2, '0')}-${dateParts[0]?.padStart(2, '0')}`;
        filtered = filtered.filter(r =>
          r.date_created?.toLowerCase().includes(formattedDate)
        );
      } else {
        filtered = filtered.filter(r =>
          r.nome_razao_social?.toLowerCase().includes(search) ||
          String(r.codigo ?? '').toLowerCase().includes(search) ||
          r.item_nome?.toLowerCase().includes(search) ||
          String(r.quantity).includes(search) ||
          String(r.id).includes(search)
        );
      }
    }

    // Filtro por período
    if (startDate && endDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(r => {
        const date = new Date(r.date_created);
        return date >= start && date <= end;
      });
    }

    setFilteredResgates(filtered);
    setCurrentPage(1);
  }, [resgates, searchFilter, actionFilter, startDate, endDate]);

  useEffect(() => {
    loadResgates();
  }, [loadResgates]);

  // Paginação
  const totalItems = filteredResgates.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedResgates = filteredResgates.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const atualizarDados = async () => {
    setSearchFilter('');
    setStartDate(undefined);
    setEndDate(undefined);
    setActionFilter('');
    setCurrentPage(1);
    await loadResgates();
    toast.success('Dados atualizados!');
  };

  // Filtro com debounce
  const applyFilter = (value: string) => {
    setSearchFilter(value);
  };

  const aplicarFiltroPorPeriodo = () => {
    // Os filtros são aplicados via useEffect
  };

  const limparDatas = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSearchFilter('');
    setActionFilter('');
    setCurrentPage(1);
  };

  // Seleção em massa
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isAllPageSelected = paginatedResgates.length > 0 && paginatedResgates.every(r => selectedIds.has(r.id));

  const toggleSelectAllPage = () => {
    if (isAllPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        paginatedResgates.forEach(r => next.delete(r.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        paginatedResgates.forEach(r => next.add(r.id));
        return next;
      });
    }
  };

  const onBulkAction = async (status: string) => {
    if (!isMatriz) {
      toast.error('Somente a Matriz pode alterar a situação dos resgates');
      return;
    }
    if (selectedIds.size === 0) return;
    try {
      setIsBulkLoading(true);
      const resp = await diamondsService.updateResgatesEmMassa(Array.from(selectedIds), status);
      toast.success(resp?.message ?? 'Resgates atualizados com sucesso');
      setSelectedIds(new Set());
      await loadResgates();
    } catch (error) {
      console.error('Erro ao atualizar em massa:', error);
      toast.error('Erro ao atualizar resgates em massa');
    } finally {
      setIsBulkLoading(false);
    }
  };

  // Alterar situação do resgate
  const onSituacaoChange = async (resgateId: number, newAction: string) => {
    if (!isMatriz) {
      toast.error('Somente a Matriz pode alterar a situação dos resgates');
      return;
    }

    try {
      const response = await diamondsService.updateResgateSolicitado(resgateId, newAction);
      if (response) {
        toast.success('Situação atualizada com sucesso');
        // Recarregar para pegar dados atualizados (inclusive estornos)
        await loadResgates();
      }
    } catch (error) {
      console.error('Erro ao atualizar situação:', error);
      toast.error('Erro ao atualizar situação');
    }
  };

  // Exportação Excel
  const exportExcel = async () => {
    try {
      toast.info('Preparando exportação Excel...');
      if (filteredResgates.length === 0) {
        toast.error('Nenhum resgate encontrado para exportar');
        return;
      }

      const XLSX = await import('xlsx');
      const data = filteredResgates.map(row => ({
        'ID': row.id,
        'Usuário': row.nome_razao_social,
        'Código': `#${row.codigo}`,
        'Item Resgatado': row.item_nome,
        'Valor (Diamantes)': row.quantity,
        'Situação': getActionLabel(row.action),
        'Data': formatDate(row.date_created),
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Resgates');
      XLSX.writeFile(wb, `Relatorio_Resgates_${format(new Date(), 'dd-MM-yyyy-HH-mm')}.xlsx`);
      toast.success('Excel exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar Excel');
    }
  };

  // Exportação PDF
  const exportPdf = async () => {
    try {
      toast.info('Preparando exportação PDF...');
      if (filteredResgates.length === 0) {
        toast.error('Nenhum resgate encontrado para exportar');
        return;
      }

      const jsPDF = (await import('jspdf')).default;
      await import('jspdf-autotable');

      const doc = new jsPDF('landscape');
      const columns = ['ID', 'Usuário', 'Código', 'Item Resgatado', 'Valor (Diamantes)', 'Situação', 'Data'];
      const body = filteredResgates.map(row => [
        row.id,
        row.nome_razao_social,
        `#${row.codigo}`,
        row.item_nome,
        row.quantity,
        getActionLabel(row.action),
        formatDate(row.date_created),
      ]);

      (doc as any).autoTable({
        theme: 'grid',
        head: [columns],
        body,
      });

      doc.save(`Relatorio_Resgates_${format(new Date(), 'dd-MM-yyyy-HH-mm')}.pdf`);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold">Aguarde</h2>
          <p className="text-muted-foreground">Carregando resgates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Relatório de Resgates</CardTitle>
              <CardDescription>
                {isMatriz
                  ? 'Gerencie as solicitações de resgates do Programa Diamantes'
                  : 'Acompanhe as solicitações de resgates do Programa Diamantes'
                }
              </CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={atualizarDados} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-1" />
                Atualizar
              </Button>
              <Button onClick={exportExcel} variant="outline" size="sm">
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                Excel
              </Button>
              <Button onClick={exportPdf} variant="outline" size="sm">
                <FileText className="h-4 w-4 mr-1" />
                PDF
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Busca */}
            <div className="flex-1 min-w-[250px]">
              <label className="text-sm font-medium mb-2 block">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nome, código, item..."
                  value={searchFilter}
                  onChange={(e) => applyFilter(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Data Início */}
            <div className="min-w-[180px]">
              <label className="text-sm font-medium mb-2 block">Data Início</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus locale={ptBR} />
                </PopoverContent>
              </Popover>
            </div>

            {/* Data Fim */}
            <div className="min-w-[180px]">
              <label className="text-sm font-medium mb-2 block">Data Fim</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus locale={ptBR} />
                </PopoverContent>
              </Popover>
            </div>

            {/* Ações */}
            <div className="flex gap-2">
              <Button onClick={aplicarFiltroPorPeriodo} disabled={!startDate || !endDate} size="sm">
                Aplicar
              </Button>
              <Button onClick={limparDatas} variant="outline" size="sm">
                <X className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Resgates ({totalItems})
            </CardTitle>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50 por pág.</SelectItem>
                <SelectItem value="100">100 por pág.</SelectItem>
                <SelectItem value="150">150 por pág.</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="px-0 md:px-6">
          {/* Barra de ação em massa */}
          {isMatriz && selectedIds.size > 0 && (
            <div className="flex items-center justify-between bg-primary/10 border border-primary/30 rounded-lg px-4 py-3 mb-4 mx-0 md:mx-0">
              <span className="text-sm font-medium">
                {selectedIds.size} resgate(s) selecionado(s)
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={isBulkLoading}
                >
                  <X className="h-4 w-4 mr-1" />
                  Limpar seleção
                </Button>
                <Button
                  size="sm"
                  className="bg-green-100 hover:bg-green-200 text-green-800 border border-green-300"
                  onClick={() => onBulkAction('resgate_aprovado')}
                  disabled={isBulkLoading}
                >
                  {isBulkLoading ? 'Processando...' : 'Aprovar todos'}
                </Button>
                <Button
                  size="sm"
                  className="bg-red-100 hover:bg-red-200 text-red-800 border border-red-300"
                  onClick={() => onBulkAction('resgate_reprovado')}
                  disabled={isBulkLoading}
                >
                  {isBulkLoading ? 'Processando...' : 'Reprovar todos'}
                </Button>
              </div>
            </div>
          )}

          {filteredResgates.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">Nenhum resgate encontrado</h3>
              <p className="text-sm text-muted-foreground">Tente ajustar os filtros.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      {isMatriz && (
                        <th className="p-3 w-10">
                          <button onClick={toggleSelectAllPage} className="flex items-center justify-center">
                            {isAllPageSelected
                              ? <CheckSquare2 className="h-4 w-4 text-primary" />
                              : <Square className="h-4 w-4 text-muted-foreground" />}
                          </button>
                        </th>
                      )}
                      <th className="text-left p-3 font-semibold">Usuário</th>
                      <th className="text-left p-3 font-semibold">Item Resgatado</th>
                      <th className="text-left p-3 font-semibold">Valor</th>
                      <th className="text-left p-3 font-semibold">Data</th>
                      <th className="text-left p-3 font-semibold">
                        <Select
                          value={actionFilter || 'todas'}
                          onValueChange={(value) => {
                            const val = value === 'todas' ? '' : value;
                            setActionFilter(val);
                          }}
                        >
                          <SelectTrigger className="w-[150px] h-8 text-xs font-semibold border-none bg-transparent shadow-none px-0 gap-1">
                            <SelectValue placeholder="Situação" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todas">Situação (Todas)</SelectItem>
                            <SelectItem value="resgate_pendente">Pendente</SelectItem>
                            <SelectItem value="resgate_aprovado">Aprovado</SelectItem>
                            <SelectItem value="resgate_reprovado">Reprovado</SelectItem>
                          </SelectContent>
                        </Select>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedResgates.map((resgate) => (
                      <tr key={resgate.id} className={cn("border-b hover:bg-muted/50 transition-colors", selectedIds.has(resgate.id) && "bg-gray-100")}>
                        {isMatriz && (
                          <td className="p-3 w-10">
                            <button onClick={() => toggleSelect(resgate.id)} className="flex items-center justify-center">
                              {selectedIds.has(resgate.id)
                                ? <CheckSquare2 className="h-4 w-4 text-primary" />
                                : <Square className="h-4 w-4 text-muted-foreground" />}
                            </button>
                          </td>
                        )}
                        {/* Usuário: Nome / Código / Tipo */}
                        <td className="p-3">
                          <div className="flex flex-col cursor-pointer" onClick={() => resgate.user_id && window.open(`/v2/usuarios/${resgate.user_id}`, '_blank')}>
                            <span className="text-primary hover:underline font-medium break-all">
                              {resgate.nome_razao_social || '-'}
                            </span>
                            <span className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                              #{resgate.codigo}
                              {resgate.role && (
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    "text-[11px] px-2 py-0.5 w-fit capitalize",
                                    resgate.role === 'revendedora' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-gray-50 text-gray-600 border-gray-300'
                                  )}
                                  style={{ marginLeft: '4px' }}
                                >
                                  {resgate.role}
                                </Badge>
                              )}
                            </span>
                          </div>
                        </td>

                        {/* Item Resgatado: Foto + Nome + ID */}
                        <td className="p-3">
                          <div
                            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => window.open(`/v2/relatorios-resgates/item/${resgate.item_id}`, '_blank')}
                            title="Ver detalhes do item"
                          >
                            <img
                              src={`${IMAGE_BASE_URL}/diamonds/item-image/${resgate.item_imagem}`}
                              alt={resgate.item_nome}
                              className="h-12 w-12 object-cover rounded-md border"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                if (!img.src.endsWith('/placeholder.svg')) {
                                  img.src = '/placeholder.svg';
                                }
                              }}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium text-primary hover:underline" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                "{resgate.item_nome}"
                              </span>
                              <span className="text-xs text-muted-foreground">ID: {resgate.item_id}</span>
                            </div>
                          </div>
                        </td>

                        {/* Valor em diamantes */}
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <span className="font-bold text-purple-600 text-base">{resgate.quantity}</span>
                            <Gem className="h-4 w-4 text-purple-600" />
                          </div>
                        </td>

                        {/* Data */}
                        <td className="p-3 text-muted-foreground">
                          {formatDate(resgate.date_created)}
                        </td>

                        {/* Situação */}
                        <td className="p-3">
                          {isMatriz ? (
                            <Select
                              value={resgate.action}
                              onValueChange={(value) => onSituacaoChange(resgate.id, value)}
                            >
                              <SelectTrigger className={cn("w-[140px] text-xs font-semibold", getActionStyles(resgate.action).badge)}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="resgate_pendente">Pendente</SelectItem>
                                <SelectItem value="resgate_reprovado">Reprovado</SelectItem>
                                <SelectItem value="resgate_aprovado">Aprovado</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline" className={cn("text-xs font-semibold", getActionStyles(resgate.action).badge)}>
                              {getActionLabel(resgate.action)}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-3 px-3">
                {paginatedResgates.map((resgate) => (
                  <Card key={resgate.id} className={cn("transition-all", getActionStyles(resgate.action).row)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div
                          className="flex flex-col cursor-pointer"
                          onClick={() => resgate.user_id && window.open(`/v2/usuarios/${resgate.user_id}`, '_blank')}
                        >
                          <span className="text-primary hover:underline font-medium text-sm break-all">
                            {resgate.nome_razao_social}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                            #{resgate.codigo}
                            {resgate.role && (
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-[11px] px-2 py-0.5 w-fit capitalize",
                                  resgate.role === 'revendedora' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-gray-50 text-gray-600 border-gray-300'
                                )}
                                style={{ marginLeft: '4px' }}
                              >
                                {resgate.role}
                              </Badge>
                            )}
                          </span>
                        </div>
                        <Badge variant="outline" className={cn("text-xs ml-2 shrink-0", getActionStyles(resgate.action).badge)}>
                          {getActionLabel(resgate.action)}
                        </Badge>
                      </div>

                      <div
                        className="flex items-center gap-3 mb-3 cursor-pointer hover:opacity-80"
                        onClick={() => navigate(`/relatorios-resgates/item/${resgate.item_id}`)}
                      >
                        <img
                          src={`${IMAGE_BASE_URL}/diamonds/item-image/${resgate.item_imagem}`}
                          alt={resgate.item_nome}
                          className="h-10 w-10 object-cover rounded-md border"
                          onError={(e) => {
                            const img = e.target as HTMLImageElement;
                            if (!img.src.endsWith('/placeholder.svg')) {
                              img.src = '/placeholder.svg';
                            }
                          }}
                        />
                        <div className="flex flex-col flex-1">
                          <span className="text-sm font-medium text-primary">{resgate.item_nome}</span>
                          <span className="text-xs text-muted-foreground">ID: {resgate.item_id}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="font-bold text-purple-600">{resgate.quantity}</span>
                          <Gem className="h-4 w-4 text-purple-600" />
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t pt-3">
                        <span className="text-xs text-muted-foreground">{formatDate(resgate.date_created)}</span>
                        {isMatriz ? (
                          <Select
                            value={resgate.action}
                            onValueChange={(value) => onSituacaoChange(resgate.id, value)}
                          >
                            <SelectTrigger className={cn("w-[130px] text-xs font-semibold h-8", getActionStyles(resgate.action).badge)}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="resgate_pendente">Pendente</SelectItem>
                              <SelectItem value="resgate_reprovado">Reprovado</SelectItem>
                              <SelectItem value="resgate_aprovado">Aprovado</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className={cn("text-xs font-semibold", getActionStyles(resgate.action).badge)}>
                            {getActionLabel(resgate.action)}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Paginação */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t gap-2 px-3">
                  <span className="text-sm text-muted-foreground">
                    Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalItems)} de {totalItems} resgates
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                    >
                      Anterior
                    </Button>
                    <span className="flex items-center text-sm px-2">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                    >
                      Próximo
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
